<template>
    <div class="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 mx-auto">
        <div
            class="wizard-topbar flex items-center justify-between gap-6 border-b border-gray-200 px-8 py-6"
        >
            <Stepper
                class="wizard-stepper"
                :steps="steps"
                :current-step="currentStep"
            />

            <div class="wizard-actions flex items-center gap-3">
                <button
                    v-if="currentStep > 2"
                    type="button"
                    class="cursor-pointer inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    @click="goBack"
                >
                    <ArrowLeft class="h-4 w-4" />
                    {{ $t("vue.back") }}
                </button>

                <button
                    type="button"
                    class="cursor-pointer btn-hover-dim inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                    :class="primaryBtnClass"
                    :disabled="primaryDisabled"
                    @click="goNextOrFinish"
                >
                    <template v-if="currentStep === 1">
                        {{ $t("vue.create_and_continue") }}
                        <ArrowRight class="h-4 w-4" />
                    </template>

                    <template v-else-if="currentStep === 2">
                        {{ $t("vue.continue_to_review") }}
                        <ArrowRight class="h-4 w-4" />
                    </template>

                    <template v-else>
                        <span>{{
                            dnsValidated
                                ? t("vue.finish_setup_process")
                                : t("vue.finish_later")
                        }}</span>
                    </template>
                </button>
            </div>
        </div>

        <div class="px-8 py-10">
            <!-- STEP 1 - ADD YOUR DOMAIN -->
            <section v-if="currentStep === 1" class="mx-auto max-w-3xl">
                <div class="text-left">
                    <h2 class="text-xl font-semibold text-gray-900">
                        {{ $t("vue.enter_your_domain") }}
                    </h2>
                    <p class="mt-2 text-sm text-gray-600">
                        {{ $t("vue.enter_your_domain_to_start_monitoring") }}
                    </p>
                </div>

                <div
                    class="mt-8 rounded-[12.8px] border border-gray-200 bg-gray-50 p-[14px]"
                >
                    <input
                        v-model.trim="domain"
                        type="text"
                        inputmode="url"
                        :placeholder="t('vue.my_beatiful_domain_co')"
                        class="mt-2 w-full rounded-[7px] border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        @blur="domainTouched = true"
                        @keydown.enter.prevent="goNextOrFinish"
                    />
                </div>

                <div
                    v-if="
                        (domainTouched || step1Error) &&
                        (step1Error || !isValidDomain(domain))
                    "
                    class="mt-4 flex items-start gap-3 rounded-[12.8px] border border-red-300 bg-red-100 px-5 py-4 text-red-600"
                >
                    <svg
                        class="mt-0.5 h-5 w-5 shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path
                            d="M10.3 3.6 1.8 18.2A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.7-2.8L13.7 3.6a2 2 0 0 0-3.4 0Z"
                        />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </svg>

                    <div class="text-sm font-medium leading-6">
                        {{ step1Error || $t("vue.invalid_domain_format") }}
                    </div>
                </div>
            </section>

            <!-- STEP 2 - CONFIGURE DNS RECORD -->
            <section v-else-if="currentStep === 2" class="mx-auto max-w-3xl">
                <!-- Header -->
                <div class="text-left">
                    <h2 class="text-2xl font-semibold text-gray-900">
                        {{ $t("vue.configure_dmarc_and_add_dns_record") }}
                    </h2>
                    <p class="mt-2 text-sm text-gray-500">
                        {{ $t("vue.set_your_dmarc_policy") }}
                    </p>
                </div>

                <!-- Domain created panel -->
                <div
                    class="mt-10 rounded-[12.8px] border border-gray-200 bg-gray-50 p-6"
                >
                    <div class="flex items-start gap-3">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-circle-check-big h-4 w-4 text-green-500 mt-[1px]"
                            aria-hidden="true"
                        >
                            <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                            <path d="m9 11 3 3L22 4"></path>
                        </svg>

                        <div>
                            <p class="text-sm font-medium text-gray-700">
                                {{ $t("vue.domain_successfully_created") }}
                            </p>
                            <span class="mt-1 inline-block -ml-[23px]">
                                <p class="text-lg font-semibold text-gray-900">
                                    {{ domain }}
                                </p>
                            </span>
                            <!--                            <p class="mt-1 text-lg font-semibold text-gray-900">-->
                            <!--                                {{ domain }}-->
                            <!--                            </p>-->
                        </div>
                    </div>
                </div>

                <!-- Policy row -->
                <div class="mt-7 grid grid-cols-1 gap-8 md:grid-cols-2">
                    <div>
                        <label
                            class="block text-sm font-semibold text-gray-800"
                        >
                            {{ $t("vue.dmarc_policy") }}
                        </label>

                        <select
                            v-model="dmarcPolicy"
                            class="mt-2 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        >
                            <option value="none">
                                None ({{ $t("vue.monitor_only") }})
                            </option>
                            <option value="quarantine">Quarantine</option>
                            <option value="reject">Reject</option>
                        </select>

                        <p class="mt-2 text-xs text-gray-400">
                            {{
                                $t(
                                    "vue.start_with_none_settings_with_no_affecting",
                                )
                            }}
                        </p>
                    </div>

                    <div>
                        <label
                            class="block text-sm font-semibold text-gray-800"
                            >{{ $t("vue.dmarc_policy_app_percentage") }}</label
                        >

                        <input
                            v-model.number="policyPct"
                            type="number"
                            min="0"
                            max="100"
                            class="mt-2 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />

                        <p class="mt-2 text-xs text-gray-400">
                            {{ $t("vue.start_one_hundred_percent_monitoring") }}
                        </p>
                    </div>
                </div>

                <!-- TXT record dark panel -->
                <div
                    class="mt-8 rounded-[12.8px] bg-[#0B1220] px-8 py-7 !text-white"
                >
                    <div
                        class="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-20"
                    >
                        <div>
                            <p class="text-xs font-medium !text-white/60">
                                Record Type
                            </p>
                            <p class="mt-1 text-sm font-semibold !text-white">
                                TXT
                            </p>
                        </div>

                        <div>
                            <p class="text-xs font-medium !text-white/60">
                                Host/Name
                            </p>
                            <p class="mt-1 text-sm font-semibold !text-white">
                                _dmarc
                            </p>
                        </div>

                        <div class="md:col-span-2">
                            <p class="text-xs font-medium !text-white/60">
                                Value
                            </p>
                            <p
                                class="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed !text-white"
                            >
                                {{ txtRecordPreview }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- DNS help collapsible row -->
                <div
                    class="mt-6 rounded-[12.8px] border border-dashed border-gray-300"
                    :class="showDnsHelp ? 'bg-[#F9FAFB]' : 'bg-white'"
                >
                    <button
                        type="button"
                        class="cursor-pointer flex w-full items-center justify-between px-6 py-5 text-left"
                        @click="showDnsHelp = !showDnsHelp"
                    >
                        <span
                            class="inline-flex items-center gap-3 text-sm font-semibold text-gray-800"
                        >
                            <Info class="h-5 w-5 text-blue-600" />
                            {{ $t("vue.how_to_update_dns_records") }}
                        </span>

                        <span class="text-sm font-medium text-gray-500">
                            {{
                                showDnsHelp
                                    ? $t("vue.collapse")
                                    : $t("vue.show")
                            }}
                        </span>
                    </button>

                    <div
                        v-if="showDnsHelp"
                        class="border-dashed border-gray-300 px-6 pb-5"
                    >
                        <ol
                            class="mt-4 list-decimal space-y-1 pl-5 text-sm text-gray-700"
                        >
                            <li>
                                {{
                                    $t(
                                        "vue.login_to_dns_provider_open_dns_zone_editor",
                                    )
                                }}
                            </li>
                            <li>
                                {{ $t("vue.create_a_new_txt_record") }}
                            </li>
                            <li>
                                <p
                                    v-html="
                                        $t('vue.set_the_host_name_to_dmarc', {
                                            dmarc: '<strong>_dmarc</strong>',
                                        })
                                    "
                                ></p>
                            </li>
                            <li>
                                {{
                                    $t(
                                        "vue.paste_the_value_shown_above_into_the_txt_value_field",
                                    )
                                }}
                            </li>
                            <li>
                                {{
                                    $t(
                                        "vue.save_your_changes_and_wait_for_dns_propagation",
                                    )
                                }}
                            </li>
                        </ol>
                    </div>
                </div>
            </section>

            <!-- STEP 3 - REVIEW & CONFIRM -->
            <section v-else class="mx-auto max-w-3xl">
                <!-- Header (left aligned) -->
                <div class="text-left">
                    <h2 class="text-2xl font-semibold text-gray-900">
                        {{ $t("vue.review_and_confirm") }}
                    </h2>
                    <p class="mt-2 text-sm text-gray-500">
                        {{ $t("vue.review_dmarc_conf_before_go_to_reports") }}
                    </p>
                </div>

                <div class="mt-8 space-y-6">
                    <div
                        class="mt-10 rounded-[12.8px] border border-gray-200 bg-gray-50 p-6"
                    >
                        <div class="flex items-start gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-check-big h-4 w-4 text-green-500 mt-[1px]"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                ></path>
                                <path d="m9 11 3 3L22 4"></path>
                            </svg>

                            <div>
                                <p class="text-sm font-medium text-gray-700">
                                    {{ $t("vue.domain_name") }}
                                </p>
                                <span class="mt-1 inline-block -ml-[23px]">
                                    <p
                                        class="text-lg font-semibold text-gray-900"
                                    >
                                        {{ domain }}
                                    </p>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Stats panel -->
                    <div
                        class="rounded-[12.8px] border border-gray-200 bg-white px-6 py-5"
                    >
                        <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                            <div>
                                <p class="text-sm font-medium text-gray-900">
                                    {{ $t("vue.created_at") }}
                                </p>
                                <p
                                    class="text-sm text-gray-500 policy-badge mt-2"
                                >
                                    {{ createdAt }}
                                </p>
                            </div>

                            <div>
                                <p class="text-sm font-medium text-gray-900">
                                    {{ $t("vue.status") }}
                                </p>
                                <span
                                    class="policy-badge mt-2"
                                    :class="
                                        dnsValidated
                                            ? 'badge-active'
                                            : 'badge-inactive'
                                    "
                                >
                                    {{ dnsValidated ? "Active" : "Inactive" }}
                                </span>
                            </div>

                            <div>
                                <p class="text-sm font-medium text-gray-900">
                                    {{ $t("vue.dmarc_policy") }}
                                </p>
                                <div>
                                    <div class="text-gray-500 text-sm mt-2">
                                        <span
                                            v-if="existingDomain?.dmarcRecord"
                                            class="policy-badge capitalize"
                                            :class="{
                                                'policy-reject':
                                                    dmarcPolicyFromDb ===
                                                    'reject',
                                                'policy-quarantine':
                                                    dmarcPolicyFromDb ===
                                                    'quarantine',
                                                'policy-none':
                                                    dmarcPolicyFromDb !==
                                                        'reject' &&
                                                    dmarcPolicyFromDb !==
                                                        'quarantine',
                                            }"
                                        >
                                            {{ dmarcPolicyFromDb }}
                                        </span>

                                        <span
                                            v-else
                                            class="policy-badge policy-none capitalize"
                                            >none</span
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Waiting / Validated callout -->
                    <div
                        class="rounded-[12.8px] border px-6 py-5"
                        :class="
                            dnsValidated
                                ? 'border-green-200 bg-green-50'
                                : 'border-yellow-200 bg-yellow-50'
                        "
                    >
                        <div class="flex items-start gap-3">
                            <!-- small dot like screenshot -->
                            <span
                                class="mt-2 inline-block h-2 w-2 rounded-full"
                                :class="
                                    dnsValidated
                                        ? 'bg-green-500'
                                        : 'bg-yellow-500'
                                "
                                aria-hidden="true"
                            />

                            <div class="flex-1">
                                <p
                                    class="text-sm font-semibold"
                                    :class="
                                        dnsValidated
                                            ? 'text-green-900'
                                            : 'text-yellow-900'
                                    "
                                >
                                    {{
                                        dnsValidated
                                            ? ""
                                            : t("vue.waiting_for_records")
                                    }}
                                </p>

                                <!-- constrain text width -->
                                <p
                                    class="max-w-[620px] text-sm leading-6"
                                    :class="
                                        dnsValidated
                                            ? 'text-green-800'
                                            : 'text-yellow-800'
                                    "
                                >
                                    <template v-if="dnsValidated">
                                        {{
                                            $t(
                                                "vue.dmarc_dns_validated_txt_instruction",
                                            )
                                        }}
                                    </template>
                                    <template v-else>
                                        {{ $t("vue.dmarc_txt_instruction") }}
                                    </template>
                                </p>

                                <!-- button placed below text, left aligned -->
                                <button
                                    v-if="!dnsValidated"
                                    type="button"
                                    class="cursor-pointer mt-4 inline-flex items-center rounded-md bg-[#088BEA] px-4 py-2 text-sm font-medium text-white"
                                    :disabled="dnsRefreshBusy"
                                    @click="refreshDns"
                                >
                                    {{
                                        dnsRefreshBusy
                                            ? t("vue.checking_progress")
                                            : t("vue.refresh_dns_record")
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ArrowLeft, ArrowRight, Check, Info } from "lucide-vue-next";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { computed, defineComponent, onMounted, ref } from "vue";
import axios from "axios";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

type Step = { key: number; title: string };

const domain = ref("");
const loading = ref(false);
const domainTouched = ref(false);
const step1Error = ref("");
const txtRecordFromServer = ref<string | null>(null);

const existingDomain = ref<DomainDto | null>(null);

type DomainDto = {
    domain: string;
    account?: string;
    active?: boolean;
    dmarcRecord?: string | null;
    created_at?: string | null;
};
const props = defineProps<{
    domainsCount?: number;
    domains?: DomainDto[];
    uuid: string;
}>();

const Stepper = defineComponent({
    name: "Stepper",
    // eslint-disable-next-line vue/no-unused-components
    components: { Check },
    props: {
        steps: {
            type: Array as () => Array<{ key: number; title: string }>,
            required: true,
        },
        currentStep: { type: Number as () => 1 | 2 | 3, required: true },
    },
    setup(props) {
        const statusOf = (key: number) =>
            key < props.currentStep
                ? "done"
                : key === props.currentStep
                  ? "active"
                  : "todo";

        const circleClass = (key: number) => {
            const s = statusOf(key);
            if (s === "done") return "bg-[#04C951] text-white";
            if (s === "active") return "bg-[#088BEA] text-white";
            return "bg-gray-200 text-gray-500";
        };

        const titleClass = (key: number) => {
            const s = statusOf(key);
            if (s === "todo") return "text-gray-400";
            return "text-gray-800";
        };

        const lineClass = (key: number) =>
            key < props.currentStep ? "bg-gray-300" : "bg-gray-200";

        const isDone = (key: number) => statusOf(key) === "done";

        return { circleClass, titleClass, lineClass, isDone };
    },
    template: `
    <div class="flex items-center gap-4">
      <div v-for="(s, idx) in steps" :key="s.key" class="flex items-center">
        <div class="flex items-center gap-3">
          <div class="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold" :class="circleClass(s.key)">
            <Check v-if="isDone(s.key)" class="h-5 w-5" />
            <span v-else>{{ s.key }}</span>
          </div>
            <div class="text-sm font-medium whitespace-nowrap" :class="titleClass(s.key)">
                {{ s.title }}
            </div>
        </div>
        <div v-if="idx !== steps.length - 1" class="mx-4 h-px w-16" :class="lineClass(s.key)"></div>
      </div>
    </div>
  `,
});

const steps: Step[] = [
    { key: 1, title: t("vue.add_your_domain") },
    { key: 2, title: t("vue.configure_dns_record") },
    { key: 3, title: t("vue.review_and_confirm") },
];

const currentStep = ref<1 | 2 | 3>(1);

const createdAt = ref<string>(new Date().toLocaleDateString());

const dmarcPolicy = ref<"none" | "quarantine" | "reject">("none");
const policyPct = ref<number>(100);
// const ruaMailbox = ref<string>("reports@rua.dmarceye.com");
// const ruaMailbox = computed(() => `${props.uuid}@rua.dmarceye.com`);

const ruaLocalPart = computed(() => {
    const v = String(props.uuid ?? "").trim();
    return v.length ? v : "reports";
});

const ruaMailbox = computed(() => `${ruaLocalPart.value}@rua.dmarceye.com`);

const showDnsHelp = ref(false);

const dnsValidated = ref(false);
const dnsRefreshBusy = ref(false);

const dmarcTxtValue = computed(() => {
    const pct = clampInt(policyPct.value, 0, 100);
    return `v=DMARC1; p=${dmarcPolicy.value}; pct=${pct}; rua=mailto:${ruaMailbox.value}; fo=1; ri=86400`;
});

const canSubmitStep1 = computed(
    () => isValidDomain(domain.value) && !loading.value,
);

const primaryDisabled = computed(() => {
    if (currentStep.value === 1) return !canSubmitStep1.value;
    if (currentStep.value === 2) return !domain.value;
    return loading.value;
});

const primaryBtnClass = computed(() => {
    if (currentStep.value === 3)
        return primaryDisabled.value ? "bg-green-300" : "bg-[#04C951]";
    return primaryDisabled.value ? "bg-gray-300" : "bg-[#088BEA]";
});

// NAVIGATION

function goBack() {
    if (currentStep.value === 2) currentStep.value = 1;
    else if (currentStep.value === 3) currentStep.value = 2;
}

function goNextOrFinish() {
    if (currentStep.value === 1) {
        addDomain();
        return;
    }

    if (currentStep.value === 2) {
        currentStep.value = 3;
        return;
    }

    router.visit(route("domains.index"));
}

// DNS CHECKER

type DnsCheckResponse = {
    success: boolean;
    result?: {
        active: boolean;
        dmarc_record?: string | null;
        dmarc_policy?: "none" | "quarantine" | "reject" | string;
    };
    checked_at?: string;
};

async function refreshDns() {
    if (!domain.value || dnsRefreshBusy.value) return;

    dnsRefreshBusy.value = true;

    try {
        const response = await axios.get<DnsCheckResponse>(
            route("domains.check-dns", { domain: domain.value }),
            { headers: { Accept: "application/json" } },
        );

        const data = response.data;
        if (!data?.success || !data.result) return;

        dnsValidated.value = Boolean(data.result.active);

        txtRecordFromServer.value = data.result.dmarc_record ?? null;

        existingDomain.value = {
            ...(existingDomain.value ?? { domain: domain.value }),
            domain: domain.value,
            active: data.result.active,
            dmarcRecord: data.result.dmarc_record ?? null,
        };

        applyRecordDefaults(txtRecordFromServer.value);

        if (dnsValidated.value) currentStep.value = 3;
    } catch (error) {
        console.error("Failed to check DNS records:", error);
    } finally {
        dnsRefreshBusy.value = false;
    }
}

// SUPPORT
function clampInt(n: number, min: number, max: number) {
    const x = Number.isFinite(n) ? Math.round(n) : min;
    return Math.min(max, Math.max(min, x));
}

// DOMAIN CREATION & CHECK

function isValidDomain(input: string): boolean {
    const v = input.trim().toLowerCase();
    return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(
        v,
    );
}

function mapDomainError(err: unknown): string {
    const e = err as Record<string, any> | undefined;
    const msgRaw = e?.domain ?? e?.errors?.domain;

    const msg = Array.isArray(msgRaw)
        ? String(msgRaw[0] ?? "")
        : String(msgRaw ?? "");

    const lower = msg.toLowerCase();

    if (
        lower.includes("taken") ||
        lower.includes("exists") ||
        lower.includes("already")
    ) {
        return "Domain already exists.";
    }

    return "Domain is invalid.";
}

function addDomain() {
    domainTouched.value = true;
    step1Error.value = "";

    if (!isValidDomain(domain.value)) {
        step1Error.value = "Domain is invalid.";
        return;
    }

    router.post(
        route("domains.store"),
        { domain: domain.value },
        {
            onBefore: () => {
                loading.value = true;
            },
            onSuccess: (page: any) => {
                const d = domain.value;
                const recordToAdd =
                    page?.props?.inactiveDomains?.[d]?.recordToAdd;

                txtRecordFromServer.value =
                    typeof recordToAdd === "string" ? recordToAdd : null;
                applyRecordDefaults(txtRecordFromServer.value);

                createdAt.value = new Date().toLocaleDateString();
                currentStep.value = 2;
            },
            onError: (err) => {
                step1Error.value = mapDomainError(err);
            },
            onFinish: () => {
                loading.value = false;
            },
            preserveScroll: true,
        },
    );
}

// EVENT LISTENER

onMounted(() => {
    const count = props.domainsCount ?? 0;
    const list = props.domains ?? [];

    if (count <= 0 || list.length === 0) {
        currentStep.value = 1;
        existingDomain.value = null;
        dnsValidated.value = false;

        recordParts.value.rua = `mailto:${ruaMailbox.value}`;
        return;
    }

    const first = list[0];
    existingDomain.value = first;

    domain.value = first.domain ?? "";
    createdAt.value = first.created_at
        ? new Date(first.created_at).toLocaleDateString()
        : createdAt.value;

    dnsValidated.value = Boolean(first.active);

    txtRecordFromServer.value = first.dmarcRecord ?? null;

    applyRecordDefaults(first.dmarcRecord ?? null);

    if (!first.dmarcRecord) {
        recordParts.value.rua = `mailto:${ruaMailbox.value}`;
    }

    currentStep.value = dnsValidated.value ? 3 : 2;
});

// DMARC RECORD PARSER EDIT

type DmarcParts = {
    rua: string;
    fo: string;
    ri: string;
};

const recordParts = ref<DmarcParts>({
    rua: `mailto:${ruaMailbox.value}`,
    fo: "1",
    ri: "86400",
});

function pickTag(record: string, tag: string): string | null {
    const re = new RegExp(`(?:^|;\\s*)${tag}\\s*=\\s*([^;]+)`, "i");
    const m = record.match(re);
    return m?.[1]?.trim() ?? null;
}

function applyRecordDefaults(record: string | null | undefined) {
    if (!record) return;

    const p = pickTag(record, "p")?.toLowerCase();
    if (p === "none" || p === "quarantine" || p === "reject") {
        dmarcPolicy.value = p;
    }

    const pctRaw = pickTag(record, "pct");
    const pct = pctRaw ? parseInt(pctRaw, 10) : NaN;
    if (Number.isFinite(pct)) policyPct.value = clampInt(pct, 0, 100);

    const rua = pickTag(record, "rua");
    if (rua) recordParts.value.rua = rua;

    const fo = pickTag(record, "fo");
    if (fo) recordParts.value.fo = fo;

    const ri = pickTag(record, "ri");
    if (ri) recordParts.value.ri = ri;
}

const txtRecordPreview = computed(() => {
    const pct = clampInt(policyPct.value, 0, 100);
    const rua = recordParts.value.rua || `mailto:${ruaMailbox.value}`;

    return `v=DMARC1; p=${dmarcPolicy.value}; pct=${pct}; rua=${rua}; fo=${recordParts.value.fo}; ri=${recordParts.value.ri}`;
});

function parseDmarcPolicyFromRecord(
    record: string | null | undefined,
): "none" | "quarantine" | "reject" {
    if (!record) return "none";

    const m = record.match(/(?:^|;\s*)p\s*=\s*(none|quarantine|reject)\b/i);
    const p = (m?.[1] ?? "none").toLowerCase();

    if (p === "reject" || p === "quarantine") return p;
    return "none";
}

const dmarcPolicyFromDb = computed<"none" | "quarantine" | "reject">(() => {
    const pDb = parseDmarcPolicyFromRecord(existingDomain.value?.dmarcRecord);
    return pDb ?? "none";
});
</script>

<style scoped>
/* BTN HOVERS */
.btn-hover-dim {
    transition:
        filter 150ms ease,
        transform 150ms ease;
}

.btn-hover-dim:hover:not(:disabled) {
    filter: brightness(0.92);
}

.btn-hover-dim:active:not(:disabled) {
    filter: brightness(0.88);
}

/* RESPONSIVITY MQ */
@media (max-width: 1000px) {
    .wizard-topbar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .wizard-actions {
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 10px;
    }
}

@media (max-width: 760px) {
    .wizard-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .wizard-actions > button {
        width: 100%;
        justify-content: center;
    }

    .wizard-stepper {
        margin: auto;
    }

    :deep(.wizard-stepper .text-sm.font-medium.whitespace-nowrap) {
        display: none;
    }

    :deep(.wizard-stepper .w-16) {
        width: 28px;
    }
}

@media (max-width: 520px) {
    .wizard-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .wizard-actions > button {
        width: 100%;
        justify-content: center;
    }

    .wizard-stepper {
        margin: auto;
    }

    :deep(.wizard-stepper .text-sm.font-medium.whitespace-nowrap) {
        display: none;
    }

    :deep(.wizard-stepper .w-16) {
        width: 28px;
    }
}
</style>
